import React, { useState, useEffect, useRef } from 'react';
import { Html5QrcodeScanner, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { supabase } from '../lib/supabase';
import Icon from '../components/AppIcon';

const QrCodeReader = () => {
  const [isScanning, setIsScanning] = useState(true);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [adminData, setAdminData] = useState(null);
  const [aziendaNome, setAziendaNome] = useState('Caricamento...');
  const [tipoTimbrata, setTipoTimbrata] = useState('ENTRATA');
  
  const [currentTime, setCurrentTime] = useState(new Date());

  const tipoRef = useRef('ENTRATA');
  useEffect(() => { tipoRef.current = tipoTimbrata; }, [tipoTimbrata]);

  // Funkcija za generisanje zvuka (Web Audio API)
  const playBeep = (type) => {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    if (type === 'success') {
      // Visok ton za uspjeh (800Hz)
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, audioCtx.currentTime); 
      gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
      oscillator.start();
      gainNode.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.5);
      oscillator.stop(audioCtx.currentTime + 0.5);
    } else {
      // Nizak, "ružan" ton za grešku (200Hz)
      oscillator.type = 'sawtooth';
      oscillator.frequency.setValueAtTime(220, audioCtx.currentTime);
      gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
      oscillator.start();
      // Dupli zvuk za grešku
      gainNode.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.2);
      oscillator.stop(audioCtx.currentTime + 0.3);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const getInitialData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profil } = await supabase
          .from('profili')
          .select('id_azienda, nome, ruolo, nome_azienda')
          .eq('id', user.id)
          .single();
        
        if (profil) {
          setAdminData(profil);
          setAziendaNome(profil.nome_azienda || 'Azienda Sconosciuta');
        }
      }
    };
    getInitialData();
  }, []);

  useEffect(() => {
    let scanner;
    if (isScanning && adminData) {
      scanner = new Html5QrcodeScanner('reader', {
        qrbox: { width: 250, height: 250 },
        fps: 30, 
        aspectRatio: 1.0,
        showTorchButtonIfSupported: true,
        formatsToSupport: [ Html5QrcodeSupportedFormats.QR_CODE ],
        videoConstraints: {
          focusMode: "continuous",
        }
      }, false);

      scanner.render(
        (result) => {
          scanner.clear().then(() => {
            setIsScanning(false);
            setStatus({ type: 'loading', message: 'Lettura in corso...' });
            handleTimbrata(result);
          }).catch(error => console.error(error));
        },
        (err) => { }
      );
    }
    return () => { if (scanner) scanner.clear().catch(e => {}); };
  }, [isScanning, adminData]);

  useEffect(() => {
    if (status.type === 'success') {
      playBeep('success'); // SVIRA ZA USPEH
      const timer = setTimeout(() => resetScanner(), 2000);
      return () => clearTimeout(timer);
    } else if (status.type === 'error') {
      playBeep('error'); // SVIRA ZA GREŠKU
    }
  }, [status.type]);

  const handleTimbrata = async (decodedText) => {
    try {
      const qrData = JSON.parse(decodedText);
      
      if (!qrData.a || qrData.a !== adminData.id_azienda) {
        setStatus({ type: 'error', message: 'Accesso Negato: Il dipendente appartiene a un\'altra azienda.' });
        return;
      }

      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);
      const noteText = `Registrata da admin: ${adminData.nome}`;

      const { data: existingEntry, error: selectError } = await supabase
        .from('presenze')
        .select('id, orario_ingresso')
        .eq('id_utente', qrData.u)
        .is('orario_uscita', null)
        .gte('orario_ingresso', startOfDay.toISOString())
        .order('orario_ingresso', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (selectError) throw selectError;

      if (tipoRef.current === 'ENTRATA') {
        if (existingEntry) {
          const oraIngresso = new Date(existingEntry.orario_ingresso).toLocaleTimeString('it-IT', { 
            hour: '2-digit', minute: '2-digit', second: '2-digit' 
          });
          
          setStatus({ 
            type: 'error', 
            message: `Timbratura già presente alle ore ${oraIngresso}. Chiudi l'uscita prima di rientrare.` 
          });
          return;
        }

        const { error } = await supabase.from('presenze').insert([{
          id_utente: qrData.u, 
          id_azienda: qrData.a,
          orario_ingresso: new Date().toISOString(),
          tipo_registrazione: 'QR_SCAN', 
          note: noteText
        }]);
        
        if (error) throw error;
        setStatus({ type: 'success', message: 'ENTRATA registrata con successo!' });

      } else {
        if (!existingEntry) {
          setStatus({ type: 'error', message: 'ERRORE: Nessuna ENTRATA aperta trovata per oggi.' });
          return;
        }

        const { error } = await supabase.from('presenze')
          .update({ orario_uscita: new Date().toISOString(), note: noteText })
          .eq('id', existingEntry.id);
        
        if (error) throw error;
        setStatus({ type: 'success', message: 'USCITA registrata con successo!' });
      }
    } catch (err) {
      console.error(err);
      setStatus({ type: 'error', message: 'Errore: QR Code non valido.' });
    }
  };

  const resetScanner = () => {
    setStatus({ type: '', message: '' });
    setIsScanning(true);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      <style>
        {`
          #reader img, #reader__status_span { display: none !important; }
          #reader { border: none !important; }
          #reader:has(video) #reader__dashboard { display: none !important; }
          #reader__camera_selection {
            width: 100% !important;
            padding: 10px !important;
            border-radius: 8px !important;
            border: 1px solid hsl(var(--border)) !important;
            background: hsl(var(--card)) !important;
            color: hsl(var(--foreground)) !important;
            margin-bottom: 10px !important;
            font-size: 12px !important;
            font-weight: bold !important;
          }
          #reader button {
            background-color: hsl(var(--primary)) !important;
            color: white !important;
            border: none !important;
            padding: 12px 24px !important;
            border-radius: 12px !important;
            font-weight: 900 !important;
            text-transform: uppercase !important;
            font-size: 10px !important;
            cursor: pointer !important;
          }
          #reader video {
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important;
            border-radius: 16px !important;
          }
        `}
      </style>

      <main className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md bg-card rounded-3xl border border-border shadow-2xl overflow-hidden">
          
          <div className="p-6 bg-primary/10 border-b border-border text-center">
            <h2 className="text-xl font-black uppercase tracking-tighter text-primary flex items-center justify-center gap-2">
              <Icon name="Camera" size={24} /> Scanner Presenze
            </h2>
            <p className="text-[10px] font-bold text-muted-foreground uppercase mt-1 tracking-widest">
              Stazione: {aziendaNome}
            </p>
          </div>

          <div className="py-4 flex flex-col items-center bg-muted/10 border-b border-border/50">
             <div className="text-2xl font-black tracking-widest text-foreground tabular-nums">
                {currentTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
             </div>
             <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">
                {currentTime.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
             </div>
          </div>

          {isScanning && (
            <div className="flex p-2 gap-2 bg-muted/20 m-4 rounded-2xl border border-border shadow-inner">
              <button 
                onClick={() => setTipoTimbrata('ENTRATA')}
                className={`flex-1 py-4 rounded-xl font-black text-xs transition-all duration-200 ${
                  tipoTimbrata === 'ENTRATA' ? 'bg-primary text-white shadow-lg' : 'bg-transparent text-muted-foreground opacity-40'
                }`}
              >ENTRATA</button>
              <button 
                onClick={() => setTipoTimbrata('USCITA')}
                className={`flex-1 py-4 rounded-xl font-black text-xs transition-all duration-200 ${
                  tipoTimbrata === 'USCITA' ? 'bg-orange-500 text-white shadow-lg' : 'bg-transparent text-muted-foreground opacity-40'
                }`}
              >USCITA</button>
            </div>
          )}

          <div className="p-6 pt-0">
            {isScanning ? (
              <div className="overflow-hidden rounded-2xl border-4 border-primary/20 bg-black aspect-square flex flex-col items-center justify-center relative text-white">
                <div id="reader" className="w-full"></div>
                <div className="absolute inset-10 border-2 border-primary/30 pointer-events-none rounded-xl">
                  <div className="absolute top-0 left-0 w-4 h-4 border-t-4 border-l-4 border-primary"></div>
                  <div className="absolute top-0 right-0 w-4 h-4 border-t-4 border-r-4 border-primary"></div>
                  <div className="absolute bottom-0 left-0 w-4 h-4 border-b-4 border-l-4 border-primary"></div>
                  <div className="absolute bottom-0 right-0 w-4 h-4 border-b-4 border-r-4 border-primary"></div>
                </div>
              </div>
            ) : (
              <div className={`p-8 rounded-2xl text-center space-y-4 animate-in zoom-in duration-300 ${status.type === 'success' ? 'bg-emerald-500/10' : 'bg-destructive/10'}`}>
                <div className="flex justify-center">
                   {status.type === 'loading' ? <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div> : <Icon name={status.type === 'success' ? 'CheckCircle2' : 'AlertTriangle'} size={64} className={status.type === 'success' ? 'text-emerald-500' : 'text-destructive'} />}
                </div>
                <h3 className={`text-lg font-black leading-tight ${status.type === 'success' ? 'text-emerald-700' : 'text-primary'}`}>
                  {status.type === 'success' ? 'SUCCESSO' : status.type === 'loading' ? 'ATTENDERE' : 'ERRORE'}
                </h3>
                <p className="text-sm font-bold opacity-80 uppercase tracking-tight italic px-2">
                  {status.message}
                </p>
                {status.type !== 'loading' && (
                  <button onClick={resetScanner} className="w-full bg-foreground text-background py-4 rounded-xl font-black uppercase tracking-widest mt-4 shadow-lg active:scale-95 transition-transform">
                    Prossimo Dipendente
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="p-4 bg-muted/30 text-center border-t border-border">
             <p className="text-[9px] text-muted-foreground uppercase font-black tracking-widest">
               Inquadra il QR Code per registrare la presenza
             </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default QrCodeReader;